// kit.jsx - Shared interface pieces for both the customer site and the dashboard.
// One file, because every one of these is small and they are always imported in
// groups. Styling lives in styles/index.css so a class can be reused outside a
// component when that is simpler.
//
// LEGEND
//    1. Button        the one button, with variants
//    2. Field         label, control, hint, error
//    3. Input / Textarea / Select / Money input
//    4. Checkbox / Toggle / RadioCards
//    5. Panel / Section / Rule
//    6. Tag           status pill
//    7. Alert         inline message
//    8. Spinner / Loading / Skeleton
//    9. Empty         nothing-here state
//   10. Modal         focus trapping dialog
//   11. Tabs          controlled tab strip
//   12. Stat          a labelled number
//   13. Toast         provider plus useToast()
//   14. CopyButton    copy to clipboard with feedback
//   15. Menu          a row of actions behind one button
//   16. SelectOrType  a dropdown that still lets somebody type

import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from "react";
import { Link } from "react-router";
import clsx from "clsx";
import { Check, Close, AlertIcon, CheckCircle, ChevronDown } from "./icons.jsx";

////////// 1. BUTTON //////////

export function Button({ variant = "primary", size, as: Tag = "button", className, loading, children, ...rest }) {
  return (
    <Tag
      className={clsx("btn", `btn-${variant}`, size && `btn-${size}`, className)}
      disabled={Tag === "button" ? rest.disabled || loading : undefined}
      {...rest}
    >
      {loading && <Spinner size={15} />}
      {children}
    </Tag>
  );
}

////////// 2. FIELD //////////

export function Field({ label, hint, error, required, children, className, htmlFor }) {
  return (
    <div className={clsx("min-w-0", className)}>
      {label && (
        <label className="label" htmlFor={htmlFor}>
          {label}
          {required && <span className="text-critical ml-0.5" aria-hidden="true">*</span>}
        </label>
      )}
      {children}
      {error ? <p className="error-text mt-1.5">{error}</p> : hint ? <p className="hint mt-1.5">{hint}</p> : null}
    </div>
  );
}

////////// 3. CONTROLS //////////
// Each control takes the object useForm().field() returns, so a form row is
// <Input {...form.field("customer.email")} label="Email" />.

export function Input({ label, hint, error, required, className, wrapClassName, prefix, suffix, ...rest }) {
  const id = useId();
  return (
    <Field label={label} hint={hint} error={error} required={required} className={wrapClassName} htmlFor={id}>
      <div className={clsx(prefix || suffix ? "relative" : "")}>
        {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted text-sm pointer-events-none">{prefix}</span>}
        <input
          id={id}
          className={clsx("field", error && "field-error", prefix && "pl-7", suffix && "pr-12", className)}
          aria-invalid={error ? "true" : undefined}
          {...rest}
        />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted text-sm pointer-events-none">{suffix}</span>}
      </div>
    </Field>
  );
}

export function Textarea({ label, hint, error, required, rows = 4, className, wrapClassName, ...rest }) {
  const id = useId();
  return (
    <Field label={label} hint={hint} error={error} required={required} className={wrapClassName} htmlFor={id}>
      <textarea id={id} rows={rows} className={clsx("field resize-y", error && "field-error", className)} {...rest} />
    </Field>
  );
}

export function Select({ label, hint, error, required, options = [], placeholder, className, wrapClassName, children, ...rest }) {
  const id = useId();
  return (
    <Field label={label} hint={hint} error={error} required={required} className={wrapClassName} htmlFor={id}>
      <div className="relative">
        <select
          id={id}
          className={clsx("field appearance-none pr-9 cursor-pointer", error && "field-error", className)}
          {...rest}
        >
          {placeholder && <option value="">{placeholder}</option>}
          {options.map((option) => (
            <option key={option.value ?? option} value={option.value ?? option}>
              {option.label ?? option}
            </option>
          ))}
          {children}
        </select>
        <svg className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-faint" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="m6 9 6 6 6-6" />
        </svg>
      </div>
    </Field>
  );
}

////////// 4. BOOLEANS AND CHOICES //////////

export function Checkbox({ label, hint, checked, onChange, error, name, className, ...rest }) {
  return (
    <label className={clsx("flex gap-3 cursor-pointer group", className)}>
      <span className="relative shrink-0 mt-0.5">
        <input type="checkbox" name={name} checked={!!checked} onChange={onChange} className="peer sr-only" {...rest} />
        <span className={clsx(
          "block w-[18px] h-[18px] border rounded-xs transition-colors",
          checked ? "bg-bronze border-bronze" : "bg-card border-rule group-hover:border-faint",
          error && "border-critical"
        )}>
          {checked && <Check size={16} className="text-white -mt-px -ml-px" />}
        </span>
      </span>
      <span className="text-sm leading-snug">
        <span className="text-ink-soft">{label}</span>
        {hint && <span className="block hint mt-0.5">{hint}</span>}
        {error && <span className="block error-text mt-0.5">{error}</span>}
      </span>
    </label>
  );
}

export function Toggle({ label, hint, checked, onChange, disabled }) {
  return (
    <label className={clsx("flex items-start justify-between gap-4", disabled ? "opacity-50" : "cursor-pointer")}>
      <span className="text-sm">
        <span className="font-medium text-ink-soft">{label}</span>
        {hint && <span className="block hint mt-0.5">{hint}</span>}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={!!checked}
        aria-label={label}
        disabled={disabled}
        onClick={() => onChange?.(!checked)}
        className={clsx(
          "relative shrink-0 w-10 h-[22px] rounded-full transition-colors mt-0.5",
          checked ? "bg-bronze" : "bg-surface-2"
        )}
      >
        <span className={clsx(
          "absolute top-[3px] w-4 h-4 bg-white rounded-full transition-all shadow-sm",
          checked ? "left-[21px]" : "left-[3px]"
        )} />
      </button>
    </label>
  );
}

// Large tappable choices. Used wherever a radio group would be too small to hit
// comfortably on a phone, which is most places on the customer site.
export function RadioCards({ label, hint, error, options, value, onChange, columns = 2, name }) {
  return (
    <Field label={label} hint={hint} error={error}>
      <div className={clsx("grid gap-2", columns === 1 ? "grid-cols-1" : columns === 3 ? "sm:grid-cols-3" : "sm:grid-cols-2")}>
        {options.map((option) => {
          const active = value === option.value;
          return (
            <label
              key={option.value}
              className={clsx(
                "flex items-start gap-3 p-3 border rounded-xs cursor-pointer transition-colors",
                active ? "border-bronze bg-bronze-wash" : "border-rule bg-card hover:border-faint"
              )}
            >
              <input type="radio" name={name} value={option.value} checked={active} onChange={() => onChange(option.value)} className="sr-only" />
              <span className={clsx("mt-1 w-[14px] h-[14px] rounded-full border-2 shrink-0", active ? "border-bronze bg-bronze" : "border-rule")}>
                {active && <span className="block w-1 h-1 bg-white rounded-full m-auto mt-[3px]" />}
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-medium text-ink">{option.label}</span>
                {option.hint && <span className="block text-[13px] text-muted mt-0.5 leading-snug">{option.hint}</span>}
              </span>
            </label>
          );
        })}
      </div>
    </Field>
  );
}

////////// 5. SURFACES //////////

export const Panel = ({ className, children, quiet, ...rest }) => (
  <div className={clsx(quiet ? "panel-quiet" : "panel", className)} {...rest}>{children}</div>
);

export function Section({ eyebrow, title, lede, children, className, align = "left", id }) {
  return (
    <section id={id} className={clsx("py-16 md:py-24", className)}>
      <div className="shell">
        {(eyebrow || title || lede) && (
          <header className={clsx("mb-10 md:mb-14", align === "center" && "text-center mx-auto")}>
            {eyebrow && <p className="eyebrow mb-3">{eyebrow}</p>}
            {title && <h2 className="display display-lg text-ink">{title}</h2>}
            {lede && <p className={clsx("lede mt-4", align === "center" ? "measure-wide mx-auto" : "measure")}>{lede}</p>}
          </header>
        )}
        {children}
      </div>
    </section>
  );
}

export const Rule = ({ className }) => <div className={clsx("rule-fade", className)} />;

////////// 6. TAG //////////

export function Tag({ tone = "neutral", children, className }) {
  const tones = { neutral: "", positive: "tag-positive", caution: "tag-caution", critical: "tag-critical", bronze: "tag-bronze" };
  return <span className={clsx("tag", tones[tone], className)}>{children}</span>;
}

////////// 7. ALERT //////////

export function Alert({ tone = "info", title, children, className, onDismiss }) {
  const tones = {
    info: "bg-surface border-rule text-ink-soft",
    success: "bg-positive-wash border-transparent text-positive",
    warning: "bg-caution-wash border-transparent text-caution",
    error: "bg-critical-wash border-transparent text-critical",
  };
  const Glyph = tone === "success" ? CheckCircle : AlertIcon;

  return (
    <div className={clsx("flex gap-3 p-3.5 border rounded-xs text-sm", tones[tone], className)} role={tone === "error" ? "alert" : "status"}>
      {tone !== "info" && <Glyph size={18} className="shrink-0 mt-px" />}
      <div className="min-w-0 flex-1">
        {title && <p className="font-semibold mb-0.5">{title}</p>}
        {children && <div className="leading-snug">{children}</div>}
      </div>
      {onDismiss && (
        <button type="button" onClick={onDismiss} className="shrink-0 opacity-60 hover:opacity-100" aria-label="Dismiss">
          <Close size={16} />
        </button>
      )}
    </div>
  );
}

////////// 8. LOADING //////////

export function Spinner({ size = 18, className }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={clsx("spin shrink-0", className)} aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" opacity="0.2" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

export const Loading = ({ label = "Loading", className }) => (
  <div className={clsx("flex items-center justify-center gap-2.5 py-16 text-muted text-sm", className)}>
    <Spinner /> {label}
  </div>
);

export const Skeleton = ({ className }) => (
  <div className={clsx("bg-surface-2 rounded-xs animate-pulse", className)} />
);

////////// 9. EMPTY //////////

export function Empty({ title, children, action, icon: Glyph, className }) {
  return (
    <div className={clsx("text-center py-16 px-6", className)}>
      {Glyph && <Glyph size={30} className="mx-auto text-faint mb-4" />}
      <p className="display display-sm text-ink">{title}</p>
      {children && <p className="hint mt-2 max-w-sm mx-auto">{children}</p>}
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

////////// 10. MODAL //////////

export function Modal({ open, onClose, title, description, children, footer, size = "md" }) {
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => { if (event.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    ref.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  const widths = { sm: "max-w-md", md: "max-w-xl", lg: "max-w-3xl", xl: "max-w-5xl" };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6">
      <div className="absolute inset-0 bg-ink/35 fade" onClick={onClose} aria-hidden="true" />
      <div
        ref={ref}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={clsx(
          // svh rather than vh: on a phone the browser toolbar counts, and a
          // dialog measured in vh puts its buttons under it.
          "relative w-full bg-card border border-rule shadow-pop rise outline-none",
          "max-h-[92svh] flex flex-col rounded-t-md sm:rounded-sm",
          widths[size]
        )}
      >
        {/* A sheet that comes up from the bottom of a phone should look like one. */}
        <span className="sheet-handle shrink-0" aria-hidden="true" />

        {(title || description) && (
          <header className="px-5 sm:px-6 pt-4 pb-4 border-b border-rule-soft shrink-0">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                {title && <h2 className="display display-sm text-ink">{title}</h2>}
                {description && <p className="hint mt-1">{description}</p>}
              </div>
              <button type="button" onClick={onClose} className="btn-ghost btn -mr-2 -mt-1" aria-label="Close">
                <Close size={18} />
              </button>
            </div>
          </header>
        )}
        <div className="px-5 sm:px-6 py-5 overflow-y-auto flex-1 min-h-0 overscroll-contain">{children}</div>
        {footer && (
          <footer className="px-5 sm:px-6 py-4 safe-bottom border-t border-rule-soft bg-surface/60 flex flex-wrap gap-2 justify-end shrink-0">
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}

////////// 11. TABS //////////

export function Tabs({ tabs, value, onChange, className }) {
  return (
    <div className={clsx("flex gap-1 border-b border-rule overflow-x-auto no-scrollbar", className)} role="tablist">
      {tabs.map((tab) => {
        const active = tab.value === value;
        return (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(tab.value)}
            className={clsx(
              // Taller than it needs to look, so it is a comfortable tap on a
              // phone as well as a target for a cursor.
              "relative px-3.5 py-3 sm:py-2.5 text-sm font-medium whitespace-nowrap transition-colors -mb-px border-b-2",
              active ? "text-ink border-bronze" : "text-muted border-transparent hover:text-ink"
            )}
          >
            {tab.label}
            {tab.count > 0 && (
              <span className={clsx("ml-1.5 text-[11px] px-1.5 py-0.5 rounded-xs", active ? "bg-bronze-wash text-bronze-dark" : "bg-surface text-faint")}>
                {tab.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

////////// 12. STAT //////////

export function Stat({ label, value, hint, tone, className }) {
  return (
    <div className={clsx("min-w-0", className)}>
      <p className="eyebrow">{label}</p>
      <p className={clsx("display display-md mt-1.5 num", tone === "positive" && "text-positive", tone === "critical" && "text-critical")}>
        {value}
      </p>
      {hint && <p className="hint mt-0.5">{hint}</p>}
    </div>
  );
}

////////// 13. TOAST //////////

const ToastContext = createContext(null);

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => setToasts((list) => list.filter((toast) => toast.id !== id)), []);

  const push = useCallback((text, tone = "success", ms = 4500) => {
    const id = Math.random().toString(36).slice(2);
    setToasts((list) => [...list, { id, text, tone }]);
    setTimeout(() => dismiss(id), ms);
    return id;
  }, [dismiss]);

  const value = useMemo(() => ({
    push,
    success: (text) => push(text, "success"),
    error: (text) => push(text, "error", 7000),
    info: (text) => push(text, "info"),
  }), [push]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-dock fixed right-3 left-3 sm:left-auto sm:right-4 z-[60] flex flex-col gap-2 pointer-events-none items-end">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={clsx(
              "pointer-events-auto rise flex items-start gap-2.5 px-4 py-3 border shadow-lift text-sm max-w-sm w-full sm:w-auto rounded-xs",
              toast.tone === "error" ? "bg-critical text-white border-transparent" : toast.tone === "info" ? "bg-ink text-paper border-transparent" : "bg-card border-rule text-ink"
            )}
            role="status"
          >
            {toast.tone === "success" && <CheckCircle size={17} className="text-positive shrink-0 mt-px" />}
            <span className="flex-1 leading-snug">{toast.text}</span>
            <button type="button" onClick={() => dismiss(toast.id)} className="opacity-60 hover:opacity-100 shrink-0" aria-label="Dismiss">
              <Close size={15} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext) || { push() {}, success() {}, error() {}, info() {} };

////////// 14. COPY //////////

export function CopyButton({ value, label = "Copy", className }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      className={clsx("btn btn-ghost btn-sm", className)}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setDone(true);
          setTimeout(() => setDone(false), 1600);
        } catch { /* clipboard blocked */ }
      }}
    >
      {done ? <Check size={15} /> : null}
      {done ? "Copied" : label}
    </button>
  );
}

////////// 15. MENU //////////
// The actions on a row, behind one button. It exists because the alternative is
// opening an order to do the one thing you already knew you wanted to do, and at
// a hundred orders a day that is a hundred round trips through a page.
//
// Items are { label, onSelect | to, icon, disabled, hint, tone }. `to` renders a
// link, so "open this order" is the same kind of thing as "print the labels" and
// the middle mouse button still works on it.
//
// The panel is positioned fixed against the button's own rectangle rather than
// absolutely inside it. That is not a preference: a table that scrolls sideways
// is a clipping box, and a menu positioned inside one gets its bottom half cut
// off. Fixed escapes that, at the cost of having to close on scroll, which is
// the right behaviour anyway once the button has moved.

export function Menu({ label = "Actions", items = [], align = "right", className, buttonClassName }) {
  const [at, setAt] = useState(null);
  const box = useRef(null);
  const open = Boolean(at);

  const place = useCallback(() => {
    const rect = box.current?.getBoundingClientRect();
    if (!rect) return;
    setAt({ top: rect.bottom + 4, left: rect.left, right: window.innerWidth - rect.right });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const away = (event) => { if (!box.current?.contains(event.target)) setAt(null); };
    const escape = (event) => { if (event.key === "Escape") setAt(null); };
    const shut = () => setAt(null);

    document.addEventListener("pointerdown", away);
    document.addEventListener("keydown", escape);
    // Capture, because the thing that scrolls is usually a container rather than
    // the window, and a menu left behind by its own button looks broken.
    window.addEventListener("scroll", shut, true);
    window.addEventListener("resize", shut);

    return () => {
      document.removeEventListener("pointerdown", away);
      document.removeEventListener("keydown", escape);
      window.removeEventListener("scroll", shut, true);
      window.removeEventListener("resize", shut);
    };
  }, [open]);

  const usable = items.filter(Boolean);
  if (!usable.length) return null;

  return (
    <div ref={box} className={clsx("relative inline-block", className)}>
      <button
        type="button"
        className={clsx("btn btn-quiet btn-sm", buttonClassName)}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => (open ? setAt(null) : place())}
      >
        {label} <ChevronDown size={14} className={clsx("transition-transform", open && "rotate-180")} />
      </button>

      {open && (
        <div
          role="menu"
          className="fixed z-50 min-w-[13rem] max-w-[16rem] bg-card border border-rule shadow-lift py-1"
          style={align === "right" ? { top: at.top, right: at.right } : { top: at.top, left: at.left }}
        >
          {usable.map((item) => {
            const Glyph = item.icon;
            const body = (
              <>
                {Glyph && <Glyph size={15} className="shrink-0 text-faint" />}
                <span className="flex-1 min-w-0">
                  <span className="block truncate">{item.label}</span>
                  {item.hint && <span className="block text-[11.5px] text-faint truncate">{item.hint}</span>}
                </span>
              </>
            );

            const shared = clsx(
              "w-full flex items-center gap-2.5 px-3 py-2 text-left text-[13.5px] transition-colors",
              item.disabled ? "text-faint cursor-not-allowed" : "text-ink-soft hover:bg-surface hover:text-ink",
              item.tone === "critical" && !item.disabled && "text-critical hover:text-critical"
            );

            if (item.to && !item.disabled) {
              return (
                <Link key={item.label} to={item.to} role="menuitem" className={shared} onClick={() => setAt(null)}>
                  {body}
                </Link>
              );
            }

            return (
              <button
                key={item.label}
                type="button"
                role="menuitem"
                disabled={item.disabled}
                className={shared}
                onClick={() => { setAt(null); item.onSelect?.(); }}
              >
                {body}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

////////// 16. SELECT OR TYPE //////////
// A dropdown for the values we know about, and a way through for the one we do
// not. Carriers add services and retire them without telling us, so a hard list
// would eventually refuse something that works on the day it appears. This
// offers the list, keeps "Something else" at the bottom, and reveals a text box
// when it is chosen or when the value already stored is not on the list.
//
// Options are { code, label, note }, which is the shape lib/carriers.js keeps.
// `anyLabel` is the empty choice, and it is a real answer rather than a
// placeholder: on EasyPost an unnamed service means price everything and take
// the cheapest.

export function SelectOrType({ label, hint, error, options = [], value = "", onChange, anyLabel, placeholder, disabled, wrapClassName }) {
  const known = options.some((option) => option.code === value);
  const [typing, setTyping] = useState(Boolean(value) && !known);

  // A value arriving from elsewhere, a template load or a leg switch, decides
  // for itself which half of this control it belongs in.
  useEffect(() => { setTyping(Boolean(value) && !options.some((option) => option.code === value)); }, [value, options]);

  const chosen = options.find((option) => option.code === value);
  const id = useId();

  return (
    <Field label={label} hint={error ? undefined : chosen?.note || hint} error={error} className={wrapClassName} htmlFor={id}>
      <div className="relative">
        <select
          id={id}
          disabled={disabled}
          className={clsx("field appearance-none pr-9", disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer", error && "field-error")}
          value={typing ? "__other" : value}
          onChange={(event) => {
            if (event.target.value === "__other") { setTyping(true); return; }
            setTyping(false);
            onChange(event.target.value);
          }}
        >
          {anyLabel && <option value="">{anyLabel}</option>}
          {options.map((option) => <option key={option.code} value={option.code}>{option.label}</option>)}
          <option value="__other">Something else</option>
        </select>
        <svg className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-faint" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="m6 9 6 6 6-6" />
        </svg>
      </div>

      {typing && (
        <input
          className={clsx("field mt-2 num", error && "field-error")}
          value={value}
          disabled={disabled}
          placeholder={placeholder || "Exactly as the provider spells it"}
          onChange={(event) => onChange(event.target.value)}
          aria-label={`${label}, typed`}
        />
      )}
    </Field>
  );
}
